import React from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Col } from 'reactstrap';
import SelectInputFieldControlled from 'components/layout/forms/SelectInputFieldControlled';
import translations from 'services/translations/translations';
import CheckboxInputField from 'components/layout/forms/CheckboxInputField';
import NumberInputField from 'components/layout/forms/NumberInputField';
import TextInputField from 'components/layout/forms/TextInputField';
import IWall from 'components/interfaces/IWall';
import { useDispatch } from 'react-redux';
import handleGlassWallProperties, {
  handleGlassWallPropertiesForType,
} from './handleGlassWallProperties';
import {
  woodyWallProfileHeightOptions,
  woodyQuantityDecorlistOptions,
} from '../../../types/woodyWallOptions';
import { setWall } from '../../../../../store/selectedWall/actions';
import handleWoodyDoorLeafColor from './handleWoodyDoorLeafColor';
import handleWoodyDoorFrameColor from './handleWoodyDoorFrameColor';
import getFieldErrorMessage from './getFieldErrorMessage';
import validateWoodyWall from './validateWoodyWall';
import CreatableDropdown from '../../../../layout/forms/CreatableDropdown';
import showOptions from '../../../../layout/forms/showOptions';
import standingWoodyProfileHeights from '../../../types/standingWoodyProfileHeights';
import { CREATABLE_DROPDOWN_EVENT } from '../../../../../services/objects/editFieldEventHandler';

const WoodyWallProperties = ({
  wall,
  disableInputs,
}: {
  wall: IWall;
  disableInputs?: boolean;
}): JSX.Element | null => {
  const editFieldsClassName = 'edit-fields';
  const { t } = useTranslation([translations.glassWallProperties]);
  const dispatch = useDispatch();

  const handleWallIntegerProperty = (event: any, propertyName: string) => {
    dispatch(
      setWall({
        ...wall,
        [propertyName]: parseInt(event.target.value, 10),
      })
    );
  };

  return (
    <div>
      <Row>
        <Col>
          <NumberInputField
            id="woodyLeftGlassDepth"
            label={t(
              `${translations.woodyWallProperties}:fields:leftGlassDepth`
            )}
            onChange={handleGlassWallProperties}
            value={wall.woodyLeftGlassDepth}
            name="woodyLeftGlassDepth"
            required
            disabled={disableInputs}
          />
        </Col>
        <Col>
          <NumberInputField
            id="woodyRightGlassDepth"
            label={t(
              `${translations.woodyWallProperties}:fields:rightGlassDepth`
            )}
            onChange={handleGlassWallProperties}
            value={wall.woodyRightGlassDepth}
            name="woodyRightGlassDepth"
            required
            disabled={disableInputs}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <NumberInputField
            id="woodyFloorGlassDepth"
            label={t(
              `${translations.woodyWallProperties}:fields:floorGlassDepth`
            )}
            onChange={handleGlassWallProperties}
            value={wall.woodyFloorGlassDepth}
            name="woodyFloorGlassDepth"
            required
            disabled={disableInputs}
          />
        </Col>
        <Col>
          <NumberInputField
            id="woodyRoofGlassDepth"
            label={t(
              `${translations.woodyWallProperties}:fields:roofGlassDepth`
            )}
            onChange={handleGlassWallProperties}
            value={wall.woodyRoofGlassDepth}
            name="woodyRoofGlassDepth"
            required
            disabled={disableInputs}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <NumberInputField
            error={getFieldErrorMessage(
              'woodyProfileWidth',
              validateWoodyWall(wall)
            )}
            id="woodyProfileWidth"
            label={t(`${translations.woodyWallProperties}:fields:profileWidth`)}
            onChange={handleGlassWallProperties}
            value={wall.woodyProfileWidth}
            name="woodyProfileWidth"
            required
            disabled={disableInputs}
          />
        </Col>
        <Col>
          <NumberInputField
            id="woodyGlassOffset"
            label={t(`${translations.woodyWallProperties}:fields:glassOffset`)}
            onChange={handleGlassWallProperties}
            value={wall.woodyGlassOffset}
            name="woodyGlassOffset"
            required
            disabled={disableInputs}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <NumberInputField
            id="woodyRoofProfileOffset"
            label={t(
              `${translations.woodyWallProperties}:fields:roofProfileOffset`
            )}
            onChange={handleGlassWallProperties}
            value={wall.woodyRoofProfileOffset}
            name="woodyRoofProfileOffset"
            required
            disabled={disableInputs}
          />
        </Col>
        <Col>
          <NumberInputField
            id="woodyFloorProfileOffset"
            label={t(
              `${translations.woodyWallProperties}:fields:floorProfileOffset`
            )}
            onChange={handleGlassWallProperties}
            value={wall.woodyFloorProfileOffset}
            name="woodyFloorProfileOffset"
            required
            disabled={disableInputs}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <SelectInputFieldControlled
            id="woodyLeftProfileHeight"
            label={t(
              `${translations.woodyWallProperties}:fields:leftProfileHeight`
            )}
            name="woodyLeftProfileHeight"
            onChange={(e: any) =>
              handleWallIntegerProperty(e, 'woodyLeftProfileHeight')
            }
            options={woodyWallProfileHeightOptions()}
            required
            value={wall.woodyLeftProfileHeight}
            disabled={disableInputs}
          />
        </Col>
        <Col>
          <SelectInputFieldControlled
            id="woodyRightProfileHeight"
            label={t(
              `${translations.woodyWallProperties}:fields:rightProfileHeight`
            )}
            name="woodyRightProfileHeight"
            onChange={(e: any) =>
              handleWallIntegerProperty(e, 'woodyRightProfileHeight')
            }
            options={woodyWallProfileHeightOptions()}
            required
            value={wall.woodyRightProfileHeight}
            disabled={disableInputs}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <SelectInputFieldControlled
            id="woodyFloorProfileHeight"
            label={t(
              `${translations.woodyWallProperties}:fields:floorProfileHeight`
            )}
            name="woodyFloorProfileHeight"
            onChange={(e: any) =>
              handleWallIntegerProperty(e, 'woodyFloorProfileHeight')
            }
            options={woodyWallProfileHeightOptions()}
            required
            value={wall.woodyFloorProfileHeight}
            disabled={disableInputs}
          />
        </Col>
        <Col>
          <SelectInputFieldControlled
            id="woodyRoofProfileHeight"
            label={t(
              `${translations.woodyWallProperties}:fields:roofProfileHeight`
            )}
            name="woodyRoofProfileHeight"
            onChange={(e: any) =>
              handleWallIntegerProperty(e, 'woodyRoofProfileHeight')
            }
            options={woodyWallProfileHeightOptions()}
            required
            value={wall.woodyRoofProfileHeight}
            disabled={disableInputs}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <NumberInputField
            id="woodyLeftGrooveDepth"
            label={t(
              `${translations.woodyWallProperties}:fields:leftGrooveDepth`
            )}
            onChange={handleGlassWallProperties}
            value={wall.woodyLeftGrooveDepth}
            name="woodyLeftGrooveDepth"
            required
            disabled={disableInputs}
          />
        </Col>
        <Col>
          <NumberInputField
            id="woodyRightGrooveDepth"
            label={t(
              `${translations.woodyWallProperties}:fields:rightGrooveDepth`
            )}
            onChange={handleGlassWallProperties}
            value={wall.woodyRightGrooveDepth}
            name="woodyRightGrooveDepth"
            required
            disabled={disableInputs}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <NumberInputField
            id="woodyFloorGrooveDepth"
            label={t(
              `${translations.woodyWallProperties}:fields:floorGrooveDepth`
            )}
            onChange={handleGlassWallProperties}
            value={wall.woodyFloorGrooveDepth}
            name="woodyFloorGrooveDepth"
            required
            disabled={disableInputs}
          />
        </Col>
        <Col>
          <NumberInputField
            id="woodyRoofGrooveDepth"
            label={t(
              `${translations.woodyWallProperties}:fields:roofProfileHeight`
            )}
            onChange={handleGlassWallProperties}
            value={wall.woodyRoofGrooveDepth}
            name="woodyRoofGrooveDepth"
            required
            disabled={disableInputs}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <CheckboxInputField
            id="woodyLeftGlassGroove"
            label={t(
              `${translations.woodyWallProperties}:fields:leftProfileGlassGrove`
            )}
            onChange={handleGlassWallProperties}
            value={wall.woodyLeftGlassGroove}
            name="woodyLeftGlassGroove"
            required
          />
        </Col>
        <Col>
          <CheckboxInputField
            id="woodyLeftGuideGroove"
            label={t(`${translations.woodyWallProperties}:fields:guideGroove`)}
            onChange={handleGlassWallProperties}
            value={wall.woodyLeftGuideGroove}
            name="woodyLeftGuideGroove"
            required
          />
        </Col>
        <Col>
          <CheckboxInputField
            id="woodyLeftBackGroove"
            label={t(`${translations.woodyWallProperties}:fields:backGroove`)}
            onChange={handleGlassWallProperties}
            value={wall.woodyLeftBackGroove}
            name="woodyLeftBackGroove"
            required
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <CheckboxInputField
            id="woodyRightGlassGroove"
            label={t(
              `${translations.woodyWallProperties}:fields:rightProfileGlassGrove`
            )}
            onChange={handleGlassWallProperties}
            value={wall.woodyRightGlassGroove}
            name="woodyRightGlassGroove"
            required
          />
        </Col>
        <Col>
          <CheckboxInputField
            id="woodyRightGuideGroove"
            label={t(`${translations.woodyWallProperties}:fields:guideGroove`)}
            onChange={handleGlassWallProperties}
            value={wall.woodyRightGuideGroove}
            name="woodyRightGuideGroove"
            required
          />
        </Col>
        <Col>
          <CheckboxInputField
            id="woodyRightBackGroove"
            label={t(`${translations.woodyWallProperties}:fields:backGroove`)}
            onChange={handleGlassWallProperties}
            value={wall.woodyRightBackGroove}
            name="woodyRightBackGroove"
            required
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <CheckboxInputField
            id="woodyFloorGlassGroove"
            label={t(
              `${translations.woodyWallProperties}:fields:floorProfileGlassGrove`
            )}
            onChange={handleGlassWallProperties}
            value={wall.woodyFloorGlassGroove}
            name="woodyFloorGlassGroove"
            required
          />
        </Col>
        <Col>
          <CheckboxInputField
            id="woodyFloorGuideGroove"
            label={t(`${translations.woodyWallProperties}:fields:guideGroove`)}
            onChange={handleGlassWallProperties}
            value={wall.woodyFloorGuideGroove}
            name="woodyFloorGuideGroove"
            required
          />
        </Col>
        <Col>
          <CheckboxInputField
            id="woodyFloorBackGroove"
            label={t(`${translations.woodyWallProperties}:fields:backGroove`)}
            onChange={handleGlassWallProperties}
            value={wall.woodyFloorBackGroove}
            name="woodyFloorBackGroove"
            required
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <CheckboxInputField
            id="woodyRoofGlassGroove"
            label={t(
              `${translations.woodyWallProperties}:fields:roofProfileGlassGrove`
            )}
            onChange={handleGlassWallProperties}
            value={wall.woodyRoofGlassGroove}
            name="woodyRoofGlassGroove"
            required
          />
        </Col>
        <Col>
          <CheckboxInputField
            id="woodyRoofGuideGroove"
            label={t(`${translations.woodyWallProperties}:fields:guideGroove`)}
            onChange={handleGlassWallProperties}
            value={wall.woodyRoofGuideGroove}
            name="woodyRoofGuideGroove"
            required
          />
        </Col>
        <Col>
          <CheckboxInputField
            id="woodyRoofBackGroove"
            label={t(`${translations.woodyWallProperties}:fields:backGroove`)}
            onChange={handleGlassWallProperties}
            value={wall.woodyRoofBackGroove}
            name="woodyRoofBackGroove"
            required
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <SelectInputFieldControlled
            id="woodyQuantityDecorList"
            label={t(`${translations.woodyWallProperties}:fields:decorListQty`)}
            name="woodyQuantityDecorList"
            onChange={(e: any) =>
              handleWallIntegerProperty(e, 'woodyQuantityDecorList')
            }
            options={woodyQuantityDecorlistOptions()}
            required
            value={wall.woodyQuantityDecorList}
            disabled={disableInputs}
          />
        </Col>
        <Col>
          <NumberInputField
            id="woodyHeightDecorList"
            label={t(
              `${translations.woodyWallProperties}:fields:woodyHeightDecor`
            )}
            onChange={handleGlassWallProperties}
            value={wall.woodyHeightDecorList}
            name="woodyHeightDecorList"
            required
            disabled={disableInputs}
          />
        </Col>
        <Col>
          <NumberInputField
            id="woodyThicknessOutsideDecorList"
            label={t(`${translations.woodyWallProperties}:fields:tOutside`)}
            onChange={handleGlassWallProperties}
            value={wall.woodyThicknessOutsideDecorList}
            name="woodyThicknessOutsideDecorList"
            required
            disabled={disableInputs}
          />
        </Col>
        <Col>
          <NumberInputField
            id="woodyThicknessInsideDecorList"
            label={t(`${translations.woodyWallProperties}:fields:tInside`)}
            onChange={handleGlassWallProperties}
            value={wall.woodyThicknessInsideDecorList}
            name="woodyThicknessInsideDecorList"
            required
            disabled={disableInputs}
          />
        </Col>
        <Col>
          <NumberInputField
            id="woodySingleHeightDecorList"
            label={t(`${translations.woodyWallProperties}:fields:singleHeight`)}
            onChange={handleGlassWallProperties}
            value={wall.woodySingleHeightDecorList}
            name="woodySingleHeightDecorList"
            required
            disabled={disableInputs}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <TextInputField
            id="woodyDoorLeafColor"
            label={t(
              `${translations.woodyWallProperties}:fields:doorLeafColor`
            )}
            onChange={handleWoodyDoorLeafColor}
            value={wall.woodyDoorLeafColor}
            name="woodyDoorLeafColor"
            required
            className={editFieldsClassName}
            disabled={disableInputs}
          />
        </Col>
        <Col>
          <TextInputField
            id="woodyDoorFrameColor"
            label={t(
              `${translations.woodyWallProperties}:fields:doorFrameColor`
            )}
            onChange={handleWoodyDoorFrameColor}
            value={wall.woodyDoorFrameColor}
            name="woodyDoorFrameColor"
            required
            className={editFieldsClassName}
            disabled={disableInputs}
          />
        </Col>
        <Col>
          <CheckboxInputField
            id="woodyIncludeWoodenLists"
            label={t(
              `${translations.woodyWallProperties}:fields:includeWoodenLists`
            )}
            onChange={handleGlassWallProperties}
            value={wall.woodyIncludeWoodenLists}
            name="woodyIncludeWoodenLists"
            required
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <CheckboxInputField
            id="woodyIncludeWoodyLeftProfile"
            label={t(
              `${translations.woodyWallProperties}:fields:includeWoodyWallLeftProfile`
            )}
            onChange={handleGlassWallProperties}
            value={wall.woodyIncludeWoodyLeftProfile}
            name="woodyIncludeWoodyLeftProfile"
            required
          />
        </Col>
        <Col>
          <CheckboxInputField
            id="woodyIncludeWoodyRightProfile"
            label={t(
              `${translations.woodyWallProperties}:fields:includeWoodyWallRightProfile`
            )}
            onChange={handleGlassWallProperties}
            value={wall.woodyIncludeWoodyRightProfile}
            name="woodyIncludeWoodyRightProfile"
            required
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <CreatableDropdown
            value={{
              label: wall.leftStandingWoodyProfileHeight,
              value: wall.leftStandingWoodyProfileHeight,
            }}
            name="leftStandingWoodyProfileHeight"
            onChange={(e: any) => {
              handleGlassWallPropertiesForType(
                e,
                CREATABLE_DROPDOWN_EVENT,
                'leftStandingWoodyProfileHeight'
              );
            }}
            options={showOptions(
              standingWoodyProfileHeights,
              translations.common,
              t
            )}
            placeholder=""
            label={t(
              `${translations.woodyWallProperties}:fields:leftStandingWoodyProfileHeight`
            )}
            disabled={false}
          />
        </Col>
        <Col>
          <CreatableDropdown
            value={{
              label: wall.rightStandingWoodyProfileHeight,
              value: wall.rightStandingWoodyProfileHeight,
            }}
            name="rightStandingWoodyProfileHeight"
            onChange={(e: any) => {
              handleGlassWallPropertiesForType(
                e,
                CREATABLE_DROPDOWN_EVENT,
                'rightStandingWoodyProfileHeight'
              );
            }}
            options={showOptions(
              standingWoodyProfileHeights,
              translations.common,
              t
            )}
            placeholder=""
            label={t(
              `${translations.woodyWallProperties}:fields:rightStandingWoodyProfileHeight`
            )}
            disabled={false}
          />
        </Col>
      </Row>
    </div>
  );
};

export default WoodyWallProperties;
